import { ServerResponse } from 'http';
import { serialize, CookieSerializeOptions } from 'cookie';

/* @description encodes a string to base64 */
export const btoa = (rawStr: string) => Buffer.from(rawStr || '').toString('base64');

/* @description decodes a base64 encoded string */
export const atob = (encodedStr: string) => Buffer.from(encodedStr || '', 'base64').toString();

/** creates a random hash consisting of characters [A-Za-z0-9] */
export const createHash = (length = 16) => {
  let hash = '';
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  for (let i = 0; i < length; i++) {
    hash += possible.charAt(Math.floor(Math.random() * possible.length));
  }

  return hash;
};

/**
 * given a string (stringified cookie data) and a cookie hash, this function
 * will encode it in a way that's easy for team BI to parse. It has this
 * structure: `hash|base64EncodedData`
 */
export function biFriendlyCookieEncode(cookieHash: string): (stringifiedCookie: string) => string {
  return (stringifiedCookie: string) => {
    return `${cookieHash}|${btoa(stringifiedCookie)}`;
  };
}

export function biFriendlyCookieDecode(encodedCookie: string): Record<string, string> | null {
  // if we don't have a cookie with two parts separated by `|` fall back to the
  // whole string (formerly used notation)
  const [_, base64Cookie = encodedCookie] = encodedCookie?.split('|') ?? [];
  const stringifiedData = atob(base64Cookie);
  try {
    return JSON.parse(stringifiedData);
  } catch {
    return null;
  }
}

export interface CookieConfig {
  name: string;
  value: unknown;
  options?: CookieSerializeOptions;
}

export const setCookies = (res: ServerResponse, cookieConfigs: CookieConfig[]) => {
  // It only happens on dev environment because dev server gets polled by the client periodically.
  if (!res.headersSent) {
    const serializedCookies = cookieConfigs.map(({ name, value, options = {} }) => {
      const stringValue = typeof value === 'object' ? 'j:' + JSON.stringify(value) : String(value);

      if ('maxAge' in options) {
        options.expires = new Date(Date.now() + options.maxAge);
        options.maxAge /= 1000;
      }
      return serialize(name, String(stringValue), options);
    });

    res.setHeader('Set-Cookie', serializedCookies);
  }
};

export const clearCookie = (res: ServerResponse, cookieName: string) => {
  res.setHeader(
    'Set-Cookie',
    serialize(cookieName, '', {
      maxAge: -1,
      path: '/',
    }),
  );
};
